import {useState} from "react";
import QuestionRenderer from "./QuestionRenderer";
import MultipleGroupRenderer from "./MultipleGroupRenderer";
import ReviewRenderer from "./ReviewRenderer";
import {CAPTCHA_SITE_KEY} from "../../util/config";
import Turnstile from "react-turnstile";

export default function DynamicForm({jsonSchema, storage, onSubmit, submitting}) {

    const saved = JSON.parse(localStorage.getItem(storage));
    const [qNumber, setQNumber] = useState(0);
    const [reviewStep, setReviewStep] = useState(false);
    const [questionAnswers, setQuestionAnswers] = useState(saved ? saved : {});
    const question = jsonSchema.questions[qNumber];
    const [validationErrors, setValidationErrors] = useState({});
    const [captchaToken, setCaptchaToken] = useState(null);

    function checkHasNextQuestion() {
        // console.log("checkHasNextQuestion");
        for (let idx = qNumber + 1; idx < jsonSchema.questions.length; idx++) {
            if (checkQuestionRequired(jsonSchema.questions[idx])) {
                return true;
            }
        }
        return false;
    }

    function findFieldValue(fieldCode) {
        for (const group of Object.values(questionAnswers)) {
            // ---- multiple group ----
            if (Array.isArray(group)) {
                for (const item of group) {
                    if (item?.[fieldCode] !== undefined) {
                        return item[fieldCode];
                    }
                }
            }

            // ---- single grouped object ----
            if (typeof group === "object" && group !== null) {
                if (group[fieldCode] !== undefined) {
                    return group[fieldCode];
                }
            }
        }
        return undefined;
    }

    function checkQuestionRequired(question) {

        if (!question.conditional) return true;

        return question.conditional.every(condition => {

            const answer = findFieldValue(condition.question);

            if (answer === undefined) return false;

            if (condition.value !== undefined) {
                return answer === condition.value;
            }

            if (Array.isArray(condition.values)) {
                return condition.values.includes(answer);
            }

            return false;
        });
    }

    const changeQuestion = (direction) => {

        // save answers after navigation
        localStorage.setItem(storage, JSON.stringify(questionAnswers));

        if (direction === 'next') {
            // validate current question first
            const isValid = validateCurrentQuestion();

            if (!isValid) {
                // do not go forward if validation fails
                return;
            }

            for (let idx = qNumber + 1; idx < jsonSchema.questions.length; idx++) {
                if (checkQuestionRequired(jsonSchema.questions[idx])) {
                    setQNumber(idx);
                    // scroll to top after changing question
                    setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }, 50);

                    return;
                }
            }
        }

        if (direction === 'prev') {
            for (let idx = qNumber - 1; idx >= 0; idx--) {
                if (checkQuestionRequired(jsonSchema.questions[idx])) {
                    setQNumber(idx);
                    return;
                }
            }
        }
    };


    const setFieldValue = (field) => {

        const updatedQuestion = {
            ...(questionAnswers[question.code] || {}),
            [field.code]: field.value
        };

        // ---- update answers ----
        const updatedAnswers = {
            ...questionAnswers,
            [question.code]: updatedQuestion
        };

        setQuestionAnswers(updatedAnswers);

        // ---- auto validation ----
        const error = validateField(field, field.value);

        setValidationErrors(prev => {

            const questionErrors = {...(prev[question.code] || {})};

            if (error) {
                questionErrors[field.code] = error;
            } else {
                delete questionErrors[field.code];
            }

            return {
                ...prev,
                [question.code]: questionErrors
            };
        });
    };


    function validateField(field, value) {

        if (field.required && (!value || value === "" || (Array.isArray(value) && value.length === 0))) {
            return "This field is required";
        }

        if (field.type === "digits" && value && field.length && value.length !== field.length) {
            return `Must be ${field.length} digits`;
        }

        if (field.pattern && value) {
            const regex = new RegExp(field.pattern);
            if (!regex.test(value)) {
                return field.patternMessage || "Invalid format";
            }
        }

        return null;
    }

    function validateCurrentQuestion() {

        // ----- MULTIPLE -----
        if (question.multiple) {

            const items = questionAnswers[question.code] || [];
            const questionErrors = [];

            items.forEach((item, index) => {

                const itemErrors = {};

                question.fields.forEach(field => {

                    const value = item?.[field.code];
                    const error = validateField(field, value);

                    if (error) {
                        itemErrors[field.code] = error;
                    }
                });

                if (Object.keys(itemErrors).length > 0) {
                    questionErrors[index] = itemErrors;
                }
            });

            if (questionErrors.length > 0) {
                setValidationErrors(prev => ({
                    ...prev,
                    [question.code]: questionErrors
                }));
                return false;
            }

            return true;
        }

        // ----- SINGLE -----
        const questionErrors = {};
        const groupAnswers = questionAnswers[question.code] || {};

        question.fields.forEach(field => {

            const value = groupAnswers[field.code];
            const error = validateField(field, value);

            if (error) {
                questionErrors[field.code] = error;
            }
        });

        if (Object.keys(questionErrors).length > 0) {
            setValidationErrors(prev => ({
                ...prev,
                [question.code]: questionErrors
            }));
            return false;
        }

        return true;
    }

    const clickSubmit = () => {
        if (!captchaToken) {
            return;
        }
        onSubmit(questionAnswers, captchaToken);
    }

    return <>

        {!reviewStep && (
            <h2 className="mb-3 text-[#0a2540] text-xl font-semibold">{question.question}</h2>
        )}

        {reviewStep && (
            <h2 className="mb-3 text-[#0a2540] text-xl font-semibold">Review answers</h2>
        )}

        <div className="text-center">
            <hr className="mt-2 mb-3"/>
        </div>

        {!reviewStep && question.multiple && (
            <MultipleGroupRenderer question={question}
                                   questionAnswers={questionAnswers}
                                   setQuestionAnswers={setQuestionAnswers}
                                   validationErrors={validationErrors[question.code] || []}
                                   setValidationErrors={setValidationErrors}
                                   validateField={validateField}
            />
        )}

        {!reviewStep && !question.multiple && (
            <QuestionRenderer question={question}
                              questionAnswers={questionAnswers}
                              setFieldValue={setFieldValue}
                              validationErrors={validationErrors[question.code]}/>
        )}

        {reviewStep && (
            <ReviewRenderer jsonSchema={jsonSchema} questionAnswers={questionAnswers}/>
        )}


        <div className="text-center">
            <hr className="mt-2 mb-3"/>
        </div>
        <div className="flex flex-wrap justify-center mt-4 gap-3">
            {!reviewStep && qNumber > 0 && (
                <button onClick={() => changeQuestion('prev')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#1C3353] text-white rounded-lg font-medium transition-all duration-300 hover:bg-gradient-to-b hover:from-[#F6D28B] hover:to-[#F1C169] hover:text-[#1C3353] hover:shadow-lg hover:shadow-yellow-500/30 group">
                    Previous
                </button>
            )}
            {!reviewStep && checkHasNextQuestion() && (
                <button onClick={() => changeQuestion('next')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#1C3353] text-white rounded-lg font-medium transition-all duration-300 hover:bg-gradient-to-b hover:from-[#F6D28B] hover:to-[#F1C169] hover:text-[#1C3353] hover:shadow-lg hover:shadow-yellow-500/30 group">
                    Next
                </button>
            )}
            {!reviewStep && !checkHasNextQuestion() && (
                <button onClick={() => {
                    const isValid = validateCurrentQuestion();
                    if (!isValid) return;
                    localStorage.setItem(storage, JSON.stringify(questionAnswers));
                    setReviewStep(true);
                    setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }, 50);
                }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#1C3353] text-white rounded-lg font-medium transition-all duration-300 hover:bg-gradient-to-b hover:from-[#F6D28B] hover:to-[#F1C169] hover:text-[#1C3353] hover:shadow-lg hover:shadow-yellow-500/30 group">
                    Review Answers
                </button>
            )}

            {reviewStep && (
                <button
                    onClick={() => setReviewStep(false)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#1C3353] text-white rounded-lg font-medium transition-all duration-300 hover:bg-gradient-to-b hover:from-[#F6D28B] hover:to-[#F1C169] hover:text-[#1C3353] hover:shadow-lg hover:shadow-yellow-500/30 group">
                    Back to questions
                </button>
            )}

            {reviewStep && (
                <button disabled={submitting}
                        onClick={clickSubmit}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#1C3353] text-white rounded-lg font-medium transition-all duration-300 hover:bg-gradient-to-b hover:from-[#F6D28B] hover:to-[#F1C169] hover:text-[#1C3353] hover:shadow-lg hover:shadow-yellow-500/30 group">
                    {submitting ? "Submitting..." : "Submit"}
                </button>
            )}

        </div>
        {reviewStep && (
            <div className="flex flex-wrap justify-center mt-4">
                <Turnstile
                    sitekey={CAPTCHA_SITE_KEY}
                    theme="light"
                    action="dynamic_forms"
                    onVerify={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => setCaptchaToken(null)}
                />
            </div>
        )}
    </>
}